import { useCallback, useContext } from 'react';
import { ElementTypes } from '@bynder-studio/render-core';
import {
    VariationsContext,
    VariationsContextData,
    VariationsThumbnailsContext,
} from 'packages/pages/design/VariationsContext/types';
import { cleanUpNewValues } from 'packages/pages/design/sidebar/variations/utils';
import useDesign from './useDesign';

type GlobalPropertyType = ElementTypes.BACKGROUND_COLOR | ElementTypes.POSTER_FRAME | ElementTypes.TRANSCRIPT;

export default function useVariations() {
    const {
        page,
        isLoading,
        isLastPage,
        currentVariation,
        updateVariationElement: updateVariationElementOriginal,
        updateVariationGlobalProperty: updateVariationGlobalPropertyOriginal,
        ...ctx
    } = useContext<VariationsContextData>(VariationsContext);

    const { creativeModel } = useDesign();
    const { variationThumbnailRenderer, swapVariationThumbnailModel } = useContext(VariationsThumbnailsContext);

    const updateVariationElement = useCallback(
        (pageId, elementId, newValues) => {
            updateVariationElementOriginal(pageId, elementId, newValues);
            updateThumbnailRenderModel(elementId, newValues, pageId);
        },
        [updateVariationElementOriginal, currentVariation, creativeModel, variationThumbnailRenderer],
    );

    const updateThumbnailRenderModel = useCallback(
        (elementId: GlobalPropertyType | number | string, newValues: any, pageId = '') => {
            if (!creativeModel) {
                return;
            }

            const model = variationThumbnailRenderer.getModel(
                currentVariation,
                pageId || creativeModel.getCurrentPageId(),
            );
            const parsedElementId = Number(elementId);
            const isGlobalProperty = isNaN(parsedElementId);
            // Text commands (updateTextSettingForSelection etc.) must not be replayed here: they recompute
            // `formattedText` from the thumbnail's own (possibly stale) text and overwrite the shared params.
            const cleanedNewValues = cleanUpNewValues(newValues);

            if (model) {
                if (isGlobalProperty) {
                    model.updateGlobalProperty(elementId as GlobalPropertyType, cleanedNewValues);
                } else {
                    model.updateElement(parsedElementId, cleanedNewValues);
                }
            }
        },
        [creativeModel, variationThumbnailRenderer, currentVariation],
    );

    const updateVariationGlobalProperty = useCallback(
        (elementType: GlobalPropertyType, elementId, newValues) => {
            const pageId = elementType === ElementTypes.TRANSCRIPT ? creativeModel?.getCurrentPageId?.() : undefined;

            // @ts-ignore
            updateVariationGlobalPropertyOriginal(elementId, newValues, pageId);
            updateThumbnailRenderModel(elementType, newValues);
        },
        [updateVariationGlobalPropertyOriginal, updateThumbnailRenderModel, creativeModel],
    );

    return {
        ...ctx,
        currentVariation,
        updateVariationElement,
        updateVariationGlobalProperty,
        isLoadingVariations: isLoading,
        variationsPage: page,
        isLastVariationsPage: isLastPage,
        updateThumbnailRenderModel,
        swapVariationThumbnailModel,
    };
}
